import { create } from 'zustand';
import { persist, PersistStorage } from 'zustand/middleware';
import { ChatMessage } from '@/types/chatTypes';

export interface ChatConversation {
  id: string;
  sessionId: string;
  messages: ChatMessage[];
  title: string; // First user message or "New Chat"
  createdAt: number;
  updatedAt: number;
}

interface ChatScreenStore {
  conversations: ChatConversation[];
  activeConversationId: string | null;

  // Conversation management
  createConversation: (
    sessionId: string,
    initialMessage?: ChatMessage
  ) => string;
  updateConversation: (conversationId: string, messages: ChatMessage[]) => void;
  addMessageToConversation: (
    conversationId: string,
    message: ChatMessage
  ) => void;
  deleteConversation: (conversationId: string) => void;
  setActiveConversation: (conversationId: string | null) => void;
  getActiveConversation: () => ChatConversation | null;
  getConversation: (conversationId: string) => ChatConversation | null;
  updateConversationId: (
    oldConversationId: string,
    newConversationId: string
  ) => void;
  clearActiveConversation: () => void;
  clearAllConversations: () => void;
  cleanupOldConversations: () => void;
}

export const useChatScreenStore = create<ChatScreenStore>()(
  persist(
    (set, get) => ({
      conversations: [],
      activeConversationId: null,

      createConversation: (sessionId, initialMessage) => {
        const now = Date.now();
        const conversationId = `conv_${now}_${Math.random().toString(36).substring(2, 15)}`;

        const title = initialMessage?.content?.substring(0, 50) || 'New Chat';

        const newConversation: ChatConversation = {
          id: conversationId,
          sessionId,
          messages: initialMessage ? [initialMessage] : [],
          title,
          createdAt: now,
          updatedAt: now,
        };

        set(() => {
          // Only keep one conversation at a time - clear all old conversations
          return {
            conversations: [newConversation],
            activeConversationId: conversationId,
          };
        });

        console.log(
          '✅ [chatScreenStore] Created new conversation (cleared old ones):',
          conversationId
        );

        return conversationId;
      },

      updateConversation: (conversationId, messages) => {
        set(state => {
          const conversation = state.conversations.find(
            c => c.id === conversationId
          );
          if (!conversation) {
            // If conversation doesn't exist, create it (shouldn't happen, but handle gracefully)
            const newConversation: ChatConversation = {
              id: conversationId,
              sessionId: conversationId, // Use conversationId as sessionId fallback
              messages,
              title:
                messages
                  .find(m => m.role === 'user')
                  ?.content?.substring(0, 50) || 'New Chat',
              createdAt: Date.now(),
              updatedAt: Date.now(),
            };
            return {
              conversations: [newConversation],
              activeConversationId: conversationId,
            };
          }

          // Update title from first user message if not set
          const firstUserMessage = messages.find(m => m.role === 'user');
          const title =
            firstUserMessage?.content?.substring(0, 50) || conversation.title;

          const updatedConversation: ChatConversation = {
            ...conversation,
            messages,
            title,
            updatedAt: Date.now(),
          };

          // Only keep this one conversation
          return {
            conversations: [updatedConversation],
            activeConversationId: conversationId,
          };
        });
      },

      addMessageToConversation: (conversationId, message) => {
        set(state => {
          const conversation = state.conversations.find(
            c => c.id === conversationId
          );
          if (!conversation) return state;

          // Update title from first user message if it's still "New Chat"
          let title = conversation.title;
          if (conversation.title === 'New Chat' && message.role === 'user') {
            title = message.content.substring(0, 50);
          }

          const updatedConversation: ChatConversation = {
            ...conversation,
            messages: [...conversation.messages, message],
            title,
            updatedAt: Date.now(),
          };

          // Only keep this one conversation
          return {
            conversations: [updatedConversation],
            activeConversationId: conversationId,
          };
        });
      },

      deleteConversation: conversationId => {
        set(state => {
          // Since we only keep one conversation, deleting it means clearing all
          if (state.conversations.some(c => c.id === conversationId)) {
            return {
              conversations: [],
              activeConversationId: null,
            };
          }
          return state;
        });
      },

      setActiveConversation: conversationId => {
        set({ activeConversationId: conversationId });
      },

      getActiveConversation: () => {
        const state = get();
        if (!state.activeConversationId) return null;
        return (
          state.conversations.find(c => c.id === state.activeConversationId) ||
          null
        );
      },

      getConversation: conversationId => {
        const state = get();
        return state.conversations.find(c => c.id === conversationId) || null;
      },

      updateConversationId: (oldConversationId, newConversationId) => {
        set(state => {
          const conversation = state.conversations.find(
            c => c.id === oldConversationId
          );
          if (!conversation) {
            console.warn(
              `[chatScreenStore] Conversation not found: ${oldConversationId}`
            );
            return state;
          }

          // Update conversation with new ID and sessionId
          const updatedConversation: ChatConversation = {
            ...conversation,
            id: newConversationId,
            sessionId: newConversationId, // Use new conversation ID as sessionId
          };

          // Update activeConversationId if it matches the old one
          const newActiveConversationId =
            state.activeConversationId === oldConversationId
              ? newConversationId
              : state.activeConversationId;

          console.log(
            `🔄 [chatScreenStore] Updated conversation ID: ${oldConversationId} -> ${newConversationId}`
          );

          return {
            conversations: [updatedConversation],
            activeConversationId: newActiveConversationId,
          };
        });
      },

      clearActiveConversation: () => {
        set({ activeConversationId: null });
      },

      clearAllConversations: () => {
        console.log('🧹 [chatScreenStore] Clearing all conversations');
        set({
          conversations: [],
          activeConversationId: null,
        });
      },

      cleanupOldConversations: () => {
        set(state => {
          // Only keep one conversation at a time - prefer active, otherwise most recent
          if (state.conversations.length <= 1) {
            return state;
          }

          const sortedConversations = [...state.conversations].sort(
            (a, b) => b.updatedAt - a.updatedAt
          );

          // Always keep the active conversation if it exists
          const activeConversation = state.activeConversationId
            ? sortedConversations.find(c => c.id === state.activeConversationId)
            : null;

          // Keep only one conversation - prefer active, otherwise most recent
          const keptConversation = activeConversation || sortedConversations[0];

          console.log(
            `🧹 [chatScreenStore] Cleaned up conversations: ${state.conversations.length} -> 1`
          );

          return {
            conversations: keptConversation ? [keptConversation] : [],
            activeConversationId: keptConversation?.id || null,
          };
        });
      },
    }),
    {
      name: 'chat-screen-store',
      // Custom storage to handle Date object serialization
      storage: {
        getItem: (name: string): string | null => {
          // Check if we're in a browser environment
          if (typeof window === 'undefined' || !window.localStorage) {
            return null;
          }
          try {
            const str = localStorage.getItem(name);
            if (!str) return null;

            // Ensure we have a string, not an object
            if (typeof str !== 'string') {
              console.error(
                'Expected string from localStorage, got:',
                typeof str,
                str
              );
              // Clear corrupted data
              if (typeof window !== 'undefined' && window.localStorage) {
                localStorage.removeItem(name);
              }
              return null;
            }

            // Check if it's already an object stringified incorrectly
            if (str === '[object Object]' || str.startsWith('[object')) {
              console.error(
                'Found corrupted object in localStorage, clearing...'
              );
              if (typeof window !== 'undefined' && window.localStorage) {
                localStorage.removeItem(name);
              }
              return null;
            }

            // Parse the JSON string
            let parsed;
            try {
              parsed = JSON.parse(str);
            } catch (parseError) {
              console.error(
                'Failed to parse JSON from localStorage:',
                parseError,
                'Value:',
                str.substring(0, 100)
              );
              // Clear corrupted data
              if (typeof window !== 'undefined' && window.localStorage) {
                localStorage.removeItem(name);
              }
              return null;
            }

            // Ensure parsed is an object
            if (typeof parsed !== 'object' || parsed === null) {
              console.error('Parsed value is not an object:', typeof parsed);
              if (typeof window !== 'undefined' && window.localStorage) {
                localStorage.removeItem(name);
              }
              return null;
            }

            // Convert timestamp strings back to Date objects
            // Only keep one conversation (the most recent or active one)
            if (
              parsed?.state?.conversations &&
              Array.isArray(parsed.state.conversations)
            ) {
              const conversations = parsed.state.conversations.map(
                (conv: ChatConversation) => ({
                  ...conv,
                  messages:
                    conv.messages?.map((msg: ChatMessage) => ({
                      ...msg,
                      timestamp:
                        typeof msg.timestamp === 'string'
                          ? new Date(msg.timestamp)
                          : msg.timestamp instanceof Date
                            ? msg.timestamp
                            : new Date(),
                    })) || [],
                })
              );

              // Only keep one conversation - prefer active, otherwise most recent
              const activeConversationId = parsed.state.activeConversationId;
              let keptConversation: ChatConversation | null = null;

              if (activeConversationId) {
                keptConversation =
                  conversations.find(c => c.id === activeConversationId) ||
                  null;
              }

              // If no active conversation found, use the most recent one
              if (!keptConversation && conversations.length > 0) {
                keptConversation = conversations.sort(
                  (a, b) => b.updatedAt - a.updatedAt
                )[0];
              }

              // Only keep one conversation
              parsed.state.conversations = keptConversation
                ? [keptConversation]
                : [];
              parsed.state.activeConversationId = keptConversation?.id || null;

              if (conversations.length > 1) {
                console.log(
                  `🧹 [chatScreenStore] Cleaned up on load: ${conversations.length} -> 1 conversation`
                );
              }
            }

            // Return the modified object as a JSON string
            return JSON.stringify(parsed);
          } catch (e) {
            console.error('Error parsing chat screen store:', e);
            // If there's an error, try to clear corrupted data
            if (typeof window !== 'undefined' && window.localStorage) {
              try {
                localStorage.removeItem(name);
              } catch (clearError) {
                console.error('Error clearing corrupted store:', clearError);
              }
            }
            return null;
          }
        },
        setItem: (name: string, value: string): void => {
          // Check if we're in a browser environment
          if (typeof window === 'undefined' || !window.localStorage) {
            return;
          }
          try {
            // Ensure value is a string - if it's an object, stringify it
            let stringValue: string;
            if (typeof value !== 'string') {
              // Zustand's persist middleware should pass strings, but handle gracefully
              // Only log in development to avoid console noise
              if (process.env.NODE_ENV === 'development') {
                console.debug(
                  'Expected string value for setItem, got:',
                  typeof value,
                  '- auto-stringifying'
                );
              }
              stringValue = JSON.stringify(value);
            } else {
              stringValue = value;
            }

            // Try to set the item
            try {
              localStorage.setItem(name, stringValue);
            } catch (quotaError) {
              // Handle quota exceeded error
              if (
                quotaError instanceof Error &&
                (quotaError.name === 'QuotaExceededError' ||
                  quotaError.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
                  quotaError.message.includes('quota'))
              ) {
                console.warn(
                  '⚠️ [chatScreenStore] localStorage quota exceeded, attempting cleanup...'
                );

                // Parse the value to get the store state
                try {
                  const parsed = JSON.parse(stringValue);
                  if (parsed?.state?.conversations) {
                    const conversations = parsed.state
                      .conversations as ChatConversation[];
                    const activeConversationId =
                      parsed.state.activeConversationId;

                    // Only keep one conversation - prefer active, otherwise most recent
                    let keptConversation: ChatConversation | null = null;

                    if (activeConversationId) {
                      keptConversation =
                        conversations.find(
                          c => c.id === activeConversationId
                        ) || null;
                    }

                    // If no active conversation found, use the most recent one
                    if (!keptConversation && conversations.length > 0) {
                      keptConversation = conversations.sort(
                        (a, b) => b.updatedAt - a.updatedAt
                      )[0];
                    }

                    // Only keep one conversation
                    const cleanedValue = JSON.stringify({
                      state: {
                        conversations: keptConversation
                          ? [keptConversation]
                          : [],
                        activeConversationId: keptConversation?.id || null,
                      },
                      version: parsed.version || 0,
                    });

                    try {
                      localStorage.setItem(name, cleanedValue);
                      console.log(
                        `✅ [chatScreenStore] Successfully saved after cleanup: ${conversations.length} -> 1 conversation`
                      );
                    } catch {
                      // If still failing, try with empty conversations
                      console.warn(
                        '⚠️ [chatScreenStore] Still quota exceeded, clearing all conversations...'
                      );
                      const emptyValue = JSON.stringify({
                        state: {
                          conversations: [],
                          activeConversationId: null,
                        },
                        version: parsed.version || 0,
                      });

                      try {
                        localStorage.setItem(name, emptyValue);
                        console.log(
                          '✅ [chatScreenStore] Successfully saved with empty conversations'
                        );
                      } catch (finalError) {
                        console.error(
                          '❌ [chatScreenStore] Failed to save even with empty conversations:',
                          finalError
                        );
                        // Last resort: clear and start fresh
                        localStorage.removeItem(name);
                      }
                    }
                  } else {
                    // If we can't parse, clear and start fresh
                    console.error(
                      '⚠️ [chatScreenStore] Could not parse store data, clearing...'
                    );
                    localStorage.removeItem(name);
                  }
                } catch (parseError) {
                  console.error(
                    '⚠️ [chatScreenStore] Error parsing store data during cleanup:',
                    parseError
                  );
                  // Clear corrupted data
                  try {
                    localStorage.removeItem(name);
                  } catch {
                    // Ignore errors when clearing
                  }
                }
              } else {
                // Re-throw if it's not a quota error
                throw quotaError;
              }
            }
          } catch (e) {
            console.error('Error setting chat screen store:', e);
          }
        },
        removeItem: (name: string): void => {
          // Check if we're in a browser environment
          if (typeof window === 'undefined' || !window.localStorage) {
            return;
          }
          try {
            localStorage.removeItem(name);
          } catch (e) {
            console.error('Error removing chat screen store:', e);
          }
        },
      } as unknown as PersistStorage<ChatScreenStore>, // Type assertion needed due to Zustand's strict typing - our implementation is correct
    }
  )
);
